import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Button as MuiButton,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { Box, spacing } from "@mui/system";
import { Delete, Edit, Search } from "@mui/icons-material";
import { Formik } from "formik";
import {
  FiscalYear,
  createFiscalYear,
  deleteFiscalYear,
  fetchFiscalYears,
  openFiscalYearsForm,
  setFiscalYear,
  updateFiscalYear,
} from "../../../redux/slices/accounting";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import { format } from "date-fns";
import useAuth from "../../../hooks/useAuth";
import { DatePicker } from "@mui/x-date-pickers";
import CustomSnackbar from "../SnackBar";
import useSnackbar from "../SnackbarHook";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { fi } from "date-fns/locale";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { toDateOnlyString } from "../../../utils/dateUtils";

const Card = styled(MuiCard)(spacing);

const CardContent = styled(MuiCardContent)(spacing);

const Button = styled(MuiButton)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Paper = styled(MuiPaper)(spacing);

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

function FiscalYearForm({
  onComplete,
  handleOpen,
}: {
  onComplete: (success: boolean) => void;
  handleOpen: () => void;
}) {
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const { fiscalYear } = useAppSelector((state) => state.accounting);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const handleSubmit = async (
    values: any,
    { resetForm, setErrors, setStatus, setSubmitting }: any
  ) => {
    try {
      const payload = {
        ...values,
        from: toDateOnlyString(values.from),
        to: toDateOnlyString(values.to),
      };

      if (fiscalYear?.id != null) {
        dispatch(
          updateFiscalYear({
            id: fiscalYear.id!,
            fiscalYear: payload,
            associationId,
          })
        );
      } else {
        dispatch(createFiscalYear({ fiscalYear: payload, associationId }));
      }

      resetForm();
      setStatus({ sent: true });
      setSubmitting(false);
      dispatch(openFiscalYearsForm());
      onComplete(true);
    } catch (e) {
      setStatus({ sent: false });
      setErrors({ submit: "Error" });
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={
        fiscalYear
          ? {
              from: fiscalYear.from,
              to: fiscalYear.to,
              active: fiscalYear.active,
            }
          : {
              from: new Date(),
              to: new Date(),
              active: true,
            }
      }
      // validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        setFieldValue,
      }) => (
        <Card mb={6}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Tilikausi
            </Typography>
            <form onSubmit={handleSubmit}>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={fi}
              >
                <Grid container spacing={6} mt={1}>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Alkaen"
                      value={values.from}
                      inputFormat="dd.MM.yyyy"
                      onChange={(val) => {
                        // if using Dayjs adapter:
                        console.log("from ->", val);
                        setFieldValue("from", val);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth variant="outlined" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Päättyy"
                      value={values.to}
                      inputFormat="dd.MM.yyyy"
                      onChange={(val) => {
                        console.log("to ->", val);
                        setFieldValue("to", val);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth variant="outlined" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">Tila</FormLabel>
                      <RadioGroup
                        row
                        aria-label="tila"
                        name="active"
                        value={values.active.toString()}
                        onChange={(event) => {
                          setFieldValue(
                            "active",
                            event.target.value === "true"
                          );
                        }}
                      >
                        <FormControlLabel
                          value="true"
                          control={<Radio />}
                          label="Avoin"
                        />
                        <FormControlLabel
                          value="false"
                          control={<Radio />}
                          label="Suljettu"
                          disabled={!fiscalYear}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      mt={3}
                      mr={2}
                    >
                      Tallenna
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      mt={3}
                      onClick={handleOpen}
                    >
                      Peruuta
                    </Button>
                  </Grid>
                </Grid>
              </LocalizationProvider>
            </form>
          </CardContent>
        </Card>
      )}
    </Formik>
  );
}

function FiscalYearList(props: any) {
  const { currentUser } = useAuth();
  const { status } = useAppSelector((state) => state.accounting);
  return (
    <Card mb={6}>
      <CardContent
        pb={1}
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Tilikaudet
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size={"small"}
          onClick={() => props.handleEditFiscalYear(undefined)}
        >
          Lisää
        </Button>
      </CardContent>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Alku Pvm</TableCell>
              <TableCell>Loppu Pvm</TableCell>
              <TableCell>Tila</TableCell>
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status === "loading" ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : props.rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Ei tilikausia
                </TableCell>
              </TableRow>
            ) : (
              props.rows.map((row: any) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    {format(new Date(row.from), "dd-MM-yyyy")}
                  </TableCell>
                  <TableCell>
                    {format(new Date(row.to), "dd-MM-yyyy")}
                  </TableCell>
                  <TableCell>{row.active ? "Auki" : "Suljettu"}</TableCell>
                  <TableCell>
                    <Search
                      style={{ cursor: "pointer" }}
                      onClick={() => console.log("show")}
                    />{" "}
                    <Edit
                      style={{ cursor: "pointer" }}
                      onClick={() => props.handleEditFiscalYear(row)}
                    />{" "}
                    {currentUser?.role === "SUPERADMIN" ? (
                      <Delete
                        style={{ cursor: "pointer" }}
                        onClick={() => props.handleDelete(row.id)}
                      />
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}

function FiscalYears() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { fiscalYears, open } = useAppSelector((state) => state.accounting);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    setProgress,
    duration,
  } = useSnackbar();

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  const handleOpen = () => {
    dispatch(openFiscalYearsForm());
  };

  const handleEditFiscalYear = (fiscalYear: FiscalYear) => {
    dispatch(setFiscalYear(fiscalYear));
    dispatch(openFiscalYearsForm());
  };

  const handleDelete = (id: number) => {
    // TODO confirm
    dispatch(deleteFiscalYear(id));
  };

  useEffect(() => {
    dispatch(fetchFiscalYears(associationId));
  }, [dispatch, associationId]);

  return (
    <React.Fragment>
      <Helmet title="Osakkaat" />
      <Typography variant="h3" gutterBottom display="inline">
        Tilikaudet
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/">
          Talous
        </Link>
        <Typography>Tilikaudet</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      {open ? (
        <FiscalYearForm
          handleOpen={handleOpen}
          onComplete={(success: any) => {
            setProgress(100);
            if (success) {
              setSnackbarMessage("Tilikausi tallennettu onnistuneesti!");
              setSnackbarSeverity("success");
            } else {
              setSnackbarMessage("Virhe tallennettaessa tilikautta.");
              setSnackbarSeverity("error");
            }
            setOpenSnackbar(true);
          }}
        />
      ) : (
        <FiscalYearList
          rows={fiscalYears}
          handleEditFiscalYear={handleEditFiscalYear}
          handleDelete={handleDelete}
          openForm={open}
        />
      )}
      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={handleCloseSnackbar}
      />
    </React.Fragment>
  );
}

export default FiscalYears;
